import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Crown, Star, Download, Share2, X } from 'lucide-react';
import axios from 'axios';
import { getProfilePicUrl } from '../utils/profilePic';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SongWarsChampionsSlideshow() {
  const [champions, setChampions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const [imageErrors, setImageErrors] = useState({});
  const [generating, setGenerating] = useState(false);
  const [previewBlob, setPreviewBlob] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const navigate = useNavigate();
  const intervalRef = useRef(null);

  useEffect(() => {
    const fetchChampions = async () => {
      try {
        const res = await axios.get(`${API}/music-review/tournament/champions-showcase`);
        const top = (res.data?.champions || []).filter(c => c.champion_wins > 0).slice(0, 6);
        setChampions(top);
      } catch { /* silent */ }
      setLoading(false);
    };
    fetchChampions();
  }, []);

  useEffect(() => {
    if (champions.length > 1 && !isPaused && !previewUrl) {
      intervalRef.current = setInterval(() => {
        setCurrentIndex(p => (p + 1) % champions.length);
      }, 6000);
    }
    return () => clearInterval(intervalRef.current);
  }, [champions.length, isPaused, previewUrl]);

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => { if (previewUrl) URL.revokeObjectURL(previewUrl); };
  }, [previewUrl]);

  const buildCardApiUrl = useCallback((c) => {
    const params = new URLSearchParams({
      username: c.artist_name || c.name || 'CHAMPION',
      wins: String(c.champion_wins || 1),
      rivals: String(c.rival_count || 0),
    });
    if (c.profile_picture) {
      params.set('profile_url', getProfilePicUrl(c.profile_picture, c.user_id));
    }
    return `${API}/champion-card/generate?${params.toString()}`;
  }, []);

  const fetchCardBlob = useCallback(async (c) => {
    const url = buildCardApiUrl(c);
    const resp = await fetch(url);
    if (!resp.ok) throw new Error('Failed to generate card');
    return await resp.blob();
  }, [buildCardApiUrl]);

  // Download/Preview: fetch image, show in modal overlay
  const handleDownload = useCallback(async () => {
    if (generating || !champions[currentIndex]) return;
    setGenerating(true);
    try {
      const blob = await fetchCardBlob(champions[currentIndex]);
      setPreviewBlob(blob);
      const objectUrl = URL.createObjectURL(blob);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(objectUrl);
    } catch (e) {
      console.error('Card generation error:', e);
    }
    setGenerating(false);
  }, [champions, currentIndex, generating, fetchCardBlob, previewUrl]);

  // Share via native Web Share API
  const handleShare = useCallback(async () => {
    if (generating || !champions[currentIndex]) return;
    setGenerating(true);
    try {
      const c = champions[currentIndex];
      const blob = previewBlob || await fetchCardBlob(c);

      if (navigator.share) {
        try {
          const file = new File([blob], 'song-wars-champion.png', { type: 'image/png' });
          if (navigator.canShare && navigator.canShare({ files: [file] })) {
            await navigator.share({
              title: `Song Wars Champion - ${c.artist_name || c.name}`,
              text: 'Check out this Song Wars Champion on OneVoiceDistro.com!',
              files: [file]
            });
            setGenerating(false);
            return;
          }
        } catch { /* user cancelled or unsupported */ }
      }

      // Fallback: show preview modal if share API unavailable
      if (!previewUrl) {
        const objectUrl = URL.createObjectURL(blob);
        setPreviewBlob(blob);
        setPreviewUrl(objectUrl);
      }
    } catch (e) {
      console.error('Share error:', e);
    }
    setGenerating(false);
  }, [champions, currentIndex, generating, fetchCardBlob, previewBlob, previewUrl]);

  const closePreview = useCallback(() => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPreviewBlob(null);
  }, [previewUrl]);

  // Save from modal (desktop fallback)
  const handleSaveFromModal = useCallback(() => {
    if (!previewUrl) return;
    const c = champions[currentIndex];
    const link = document.createElement('a');
    link.href = previewUrl;
    link.download = `song-wars-champion-${(c?.artist_name || 'card').replace(/\s+/g, '-').toLowerCase()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [previewUrl, champions, currentIndex]);

  // Share from modal
  const handleShareFromModal = useCallback(async () => {
    if (!previewBlob) return;
    const c = champions[currentIndex];
    if (navigator.share) {
      try {
        const file = new File([previewBlob], 'song-wars-champion.png', { type: 'image/png' });
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({
            title: `Song Wars Champion - ${c?.artist_name || c?.name}`,
            text: 'Check out this Song Wars Champion on OneVoiceDistro.com!',
            files: [file]
          });
          return;
        }
      } catch { /* cancelled */ }
    }
  }, [previewBlob, champions, currentIndex]);

  if (loading) {
    return (
      <div className="py-12 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#d4a843] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (champions.length === 0) return null;

  const c = champions[currentIndex];

  return (
    <div
      className="py-10 px-4"
      data-testid="song-wars-champions"
    >
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#d4a843]/10 border border-[#d4a843]/30 mb-4">
            <Crown size={16} className="text-[#d4a843]" />
            <span className="text-xs font-mono tracking-[0.3em] uppercase text-[#d4a843]">Song Wars</span>
          </div>
          <h2 className="text-2xl md:text-3xl font-bold uppercase">
            <span className="champion-title-text">Hall of Champions</span>
          </h2>
        </div>

        {/* Champion Card */}
        <div className="relative">
          {/* Prev/next arrow overlays removed (iter_338h) — swipe + dot
              indicators below handle navigation. Arrows were overlapping
              the card edges on mobile and partially covering the champion
              artwork. */}

          <div
            className="champion-card-wrapper cursor-pointer relative overflow-hidden"
            onClick={() => c.user_id && navigate(`/profile/${c.user_id}`)}
            data-testid={`champion-${currentIndex}`}
          >
            <div className="champion-card-top">
              <img src="/champion-bg.png" alt="" className="w-full h-full object-cover" draggable={false} />
              <div className="champion-user-overlay">
                <div className="champion-photo-frame">
                  {(c.profile_picture || (c.has_profile_picture && c.user_id)) && !imageErrors[c.name] ? (
                    <img
                      src={c.profile_picture 
                        ? getProfilePicUrl(c.profile_picture, c.user_id) 
                        : `${API}/users/${c.user_id}/profile-picture`}
                      alt={c.artist_name || c.name}
                      className="w-full h-full object-cover"
                      onError={() => setImageErrors(p => ({ ...p, [c.name]: true }))}
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-[#2a2a2a] to-[#1a1a1a] flex items-center justify-center">
                      <Crown size={36} className="text-[#d4a843]" />
                    </div>
                  )}
                </div>
                <div className="champion-vip-badge">
                  <Crown size={11} className="text-[#d4a843]" />
                  <span>VIP</span>
                </div>
                <div className="champion-tags-row">
                  {c.champion_wins > 0 && (
                    <div className="champion-tag"><Crown size={10} /><span>CHAMPION</span></div>
                  )}
                  {c.rival_count > 0 && (
                    <div className="champion-tag"><Star size={10} /><span>FEATURED</span></div>
                  )}
                </div>
                <div className="champion-username">{(c.artist_name || c.name || '').toUpperCase()}</div>
              </div>
            </div>
            <div className="champion-bottom-banner">
              <div className="champion-sw-script">Song Wars</div>
              <div className="champion-title-3d">CHAMPION</div>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex items-center justify-center gap-3 mt-4">
            <button
              onClick={handleDownload}
              disabled={generating}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#d4a843]/10 border border-[#d4a843]/40 text-[#d4a843] font-mono text-xs uppercase tracking-wider hover:bg-[#d4a843]/20 hover:border-[#d4a843] transition-all disabled:opacity-50"
              data-testid="share-champion-card-btn"
            >
              {generating ? (
                <div className="w-4 h-4 border-2 border-[#d4a843] border-t-transparent rounded-full animate-spin" />
              ) : (
                <Download size={14} />
              )}
              {generating ? 'Generating...' : 'Download Card'}
            </button>
            <button
              onClick={handleShare}
              disabled={generating}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#d4a843] text-black font-mono text-xs uppercase tracking-wider font-bold hover:bg-[#f0d68a] transition-all disabled:opacity-50"
              data-testid="share-champion-social-btn"
            >
              <Share2 size={14} />
              Share
            </button>
          </div>

          {/* Dots */}
          {champions.length > 1 && (
            <div className="flex items-center justify-center gap-2 mt-4">
              {champions.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentIndex(i)}
                  className={`h-1.5 transition-all ${i === currentIndex ? 'w-6 bg-[#d4a843]' : 'w-1.5 bg-[#333] hover:bg-[#555]'}`}
                  data-testid={`champions-dot-${i}`}
                />
              ))}
            </div>
          )}
        </div>

        {/* Leaderboard link */}
        <div className="text-center mt-6">
          <button
            onClick={() => navigate('/song-wars/leaderboard')}
            className="text-[#d4a843] font-mono text-sm hover:underline inline-flex items-center gap-2"
            data-testid="view-full-leaderboard-btn"
          >
            View Full Song Wars Leaderboard <ChevronRight size={16} />
          </button>
        </div>
      </div>

      {/* ====== Image Preview Modal ====== */}
      {previewUrl && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
          onClick={closePreview}
          data-testid="champion-card-preview-modal"
        >
          <div
            className="relative max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={closePreview}
              className="absolute -top-10 right-0 text-white/70 hover:text-white"
              data-testid="close-preview-modal"
            >
              <X size={28} />
            </button>

            {/* The generated card image */}
            <img
              src={previewUrl}
              alt="Song Wars Champion Card"
              className="w-full rounded border-2 border-[#d4a843]"
              data-testid="champion-card-preview-image"
            />

            {/* Instruction text */}
            <p className="text-center text-white/60 text-xs font-mono mt-3">
              Long-press image to save &bull; or use buttons below
            </p>

            {/* Modal action buttons */}
            <div className="flex items-center justify-center gap-3 mt-3">
              <button
                onClick={handleSaveFromModal}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#d4a843]/10 border border-[#d4a843]/40 text-[#d4a843] font-mono text-xs uppercase tracking-wider hover:bg-[#d4a843]/20 transition-all"
                data-testid="modal-save-btn"
              >
                <Download size={14} /> Save Image
              </button>
              <button
                onClick={handleShareFromModal}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#d4a843] text-black font-mono text-xs uppercase tracking-wider font-bold hover:bg-[#f0d68a] transition-all"
                data-testid="modal-share-btn"
              >
                <Share2 size={14} /> Share
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
